/**
 * Pannello "lista filtrata" per la modalità Dual dell'Abbinamento.
 * Riutilizzato sia per macchine che articoli con tipi diversi.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Eye } from 'lucide-react';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useAbbinatoreFacets } from '../hooks/useAbbinatoreFacets';
import { FacetDomain } from '../api';
import { Combobox } from './Combobox';

export interface PickerItem {
  id: string;
  primary: string;     // testo principale (sku / model)
  secondary: string;   // testo secondario (desc / brand)
  meta?: string | null; // tipo/category
  /** Etichetta della relazione col padre, mostrata sulle righe figlie. */
  kindLabel?: string | null;
  /** Figlie sempre visibili sotto il padre (mai un menu espandibile). */
  children?: PickerItem[];
  /** Riga originale, passata a ``onOpenDetail`` senza reinterpretarla. */
  data?: unknown;
}

export interface PickerFetchResp {
  items: PickerItem[];
  total: number;
}

interface Props {
  title: string;
  testId: string;
  selected: Set<string>;
  onToggle: (id: string) => void;
  /** Seleziona/deseleziona in blocco (riga padre con le sue figlie). */
  onToggleMany: (ids: string[], select: boolean) => void;
  onClear: () => void;
  /** Dominio dei filtri: decide sia la sorgente delle marche sia se il
   *  secondo filtro è "tipologie" (macchine) o "categorie" (articoli). */
  facetDomain: FacetDomain;
  brandLabel?: string;
  secondaryLabel?: string;
  searchPlaceholder: string;
  // Fetcher: dato i filtri restituisce items (paginato)
  fetcher: (params: {
    search?: string; brand?: string; category?: string; page: number; pageSize: number;
  }) => Promise<PickerFetchResp>;
  /** Se passato, mostra l'icona occhio a fianco del nome per aprire la scheda. */
  onOpenDetail?: (item: PickerItem) => void;
  /** Titolo del pulsante/tooltip della scorciatoia. */
  detailTitle?: string;
}

const PAGE_SIZE = 80;
const ROW_HEIGHT = 32;

export function DualPicker({
  title, testId, selected, onToggle, onToggleMany, onClear,
  facetDomain,
  brandLabel = 'Marca',
  secondaryLabel = 'Categoria',
  searchPlaceholder, fetcher,
  onOpenDetail, detailTitle = 'Vedi dettaglio macchina',
}: Props) {
  const [brand, setBrand] = useState<string | null>(null);
  const [secondary, setSecondary] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);

  // Scelta la marca, il secondo filtro mostra solo i valori presenti sotto
  // di essa; se quello attivo sparisce viene azzerato dall'hook.
  const facets = useAbbinatoreFacets(facetDomain, brand, {
    value: secondary, onChange: setSecondary,
  });
  const brandOpts = facets.marche;
  const secondaryOpts = facetDomain === 'machines' ? facets.tipologie : facets.categorie;

  const [items, setItems] = useState<PickerItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const filterSig = useMemo(
    () => JSON.stringify([debounced, brand, secondary]),
    [debounced, brand, secondary],
  );

  useEffect(() => {
    setItems([]); setPage(0); setHasMore(true);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [filterSig]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetcher({
      search: debounced || undefined,
      brand: brand || undefined,
      category: secondary || undefined,
      page, pageSize: PAGE_SIZE,
    }).then(r => {
      if (cancel) return;
      setItems(prev => page === 0 ? r.items : [...prev, ...r.items]);
      setTotal(r.total);
      setHasMore((page + 1) * PAGE_SIZE < r.total && r.items.length > 0);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [filterSig, page]);  // eslint-disable-line react-hooks/exhaustive-deps

  useInfiniteScroll(sentinelRef, () => {
    if (!loading && hasMore) setPage(p => p + 1);
  }, hasMore && !loading);

  return (
    <div className="flex flex-col bg-white border border-[#e2e2df] rounded-[11px] overflow-hidden flex-1 min-h-0" data-testid={testId}>
      {/* Header */}
      <div className="px-3.5 py-3 border-b border-[#eee] flex items-center gap-2 flex-none">
        <div className="text-[15px] font-semibold text-[#1f1f1d]">{title}</div>
        <div className="text-[13px] font-mono text-[#5c5c57]">{total.toLocaleString('it')}</div>
        <div className="flex-1" />
        {selected.size > 0 && (
          <>
            <span className="text-[13px] font-semibold text-[#2b3a8f]">{selected.size} sel.</span>
            <button onClick={onClear} className="text-[13px] text-[#5c6bb0] hover:underline">deseleziona</button>
          </>
        )}
      </div>
      {/* Filters */}
      <div className="px-3.5 py-2.5 border-b border-[#f1f1ee] grid grid-cols-3 gap-2 flex-none">
        <Combobox value={brand} onChange={setBrand} options={brandOpts} placeholder={`Tutte ${brandLabel.toLowerCase()}`} testId={`${testId}-brand`} />
        <Combobox value={secondary} onChange={setSecondary} options={secondaryOpts} placeholder={`Tutte ${secondaryLabel.toLowerCase()}`} testId={`${testId}-secondary`} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          data-testid={`${testId}-search`}
          className="px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] text-[14px] focus:outline-none focus:border-[#2b3a8f]"
        />
      </div>
      {/* List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {items.length === 0 && !loading ? (
          <div className="px-4 py-16 text-center text-[14px] text-[#5c5c57]">Nessun risultato</div>
        ) : (
          <>
            {items.flatMap(it => {
              // Il padre comanda il gruppo: selezionarlo porta dentro le sue
              // figlie, che sono lì sotto e quasi sempre vanno insieme. Le
              // singole restano deselezionabili una per una, e finché la
              // selezione è parziale la casella del padre resta indeterminata
              // invece di mentire dicendo "tutto" o "niente".
              const childIds = (it.children ?? []).map(c => c.id);
              const groupIds = [it.id, ...childIds];
              const nSel = groupIds.filter(id => selected.has(id)).length;
              const allSel = nSel === groupIds.length;
              return [
                <Row
                  key={it.id}
                  item={it}
                  depth={0}
                  testId={testId}
                  selected={selected.has(it.id)}
                  checked={allSel}
                  indeterminate={nSel > 0 && !allSel}
                  onToggle={() => onToggleMany(groupIds, !allSel)}
                  onOpenDetail={onOpenDetail}
                  detailTitle={detailTitle}
                />,
                ...(it.children ?? []).map(ch => (
                  <Row
                    key={`${it.id}/${ch.id}`}
                    item={ch}
                    depth={1}
                    testId={testId}
                    selected={selected.has(ch.id)}
                    checked={selected.has(ch.id)}
                    onToggle={() => onToggle(ch.id)}
                    onOpenDetail={onOpenDetail}
                    detailTitle={detailTitle}
                  />
                )),
              ];
            })}
            <div ref={sentinelRef} className="h-7 flex items-center justify-center text-[12px] font-mono text-[#5c5c57]">
              {loading ? '…' : hasMore ? '↓ scorri' : `fine · ${items.length}`}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Riga singola — padre (``depth`` 0) o figlia già espansa (``depth`` 1).
   Il click sulla riga fa solo selezione: la scheda si apre dall'icona
   occhio, così non c'è modo di aprirla per sbaglio scorrendo la lista.
───────────────────────────────────────────────────────────────── */
function Row({
  item, depth, testId, selected, checked, indeterminate = false,
  onToggle, onOpenDetail, detailTitle,
}: {
  item: PickerItem;
  depth: 0 | 1;
  testId: string;
  /** La riga stessa è selezionata (guida l'evidenziazione). */
  selected: boolean;
  /** Stato della casella: per un padre riguarda l'intero gruppo. */
  checked: boolean;
  indeterminate?: boolean;
  onToggle: () => void;
  onOpenDetail?: (item: PickerItem) => void;
  detailTitle: string;
}) {
  const isChild = depth === 1;
  const boxRef = useRef<HTMLInputElement>(null);

  // `indeterminate` esiste solo come proprietà del nodo, non come attributo.
  useEffect(() => {
    if (boxRef.current) boxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div
      onClick={onToggle}
      data-testid={`${testId}-item-${item.id}`}
      style={{ height: ROW_HEIGHT, paddingLeft: isChild ? 26 : 12 }}
      className={`flex items-center gap-2 pr-3 border-b border-[#f1f1ee] cursor-pointer text-[14px] select-none transition-colors ${
        selected ? 'bg-[#eef1fb]' : isChild ? 'bg-[#fcfcfb] hover:bg-[#f6f6f4]' : 'hover:bg-[#fafafa]'
      }`}
    >
      {isChild && <span className="flex-none text-[12px] font-mono text-[#a8a8a2] leading-none">└</span>}
      <input
        ref={boxRef}
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        onClick={e => e.stopPropagation()}
        className="flex-none"
      />
      {/* Nome e occhio stanno insieme: l'icona segue il testo invece di
          galleggiare a distanza fissa dai modelli con nome corto. */}
      <span className="flex items-center gap-1.5 min-w-0 max-w-[55%]">
        <span className={`font-mono text-[13px] truncate ${selected ? 'text-[#2b3a8f] font-semibold' : 'text-[#5c5c57]'}`}>{item.primary}</span>
        {onOpenDetail && (
          <button
            onClick={e => { e.stopPropagation(); onOpenDetail(item); }}
            title={detailTitle}
            aria-label={detailTitle}
            data-testid={`${testId}-detail-${item.id}`}
            className="flex-none text-[#9b9b96] hover:text-[#2b3a8f] transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
      <span className="flex-1 truncate text-[#3a3a36]">{item.secondary}</span>
      {item.kindLabel && (
        <span className="flex-none text-[11px] font-mono uppercase tracking-wide text-[#6d6d68] bg-[#f1f1ee] border border-[#e4e4e1] px-1.5 py-[1px] rounded">
          {item.kindLabel}
        </span>
      )}
      {item.meta && <span className="text-[12px] text-[#5c5c57] font-mono">{item.meta}</span>}
    </div>
  );
}
